import type { ClientResponse, UpdateClientRequest } from '../types/dtos';

export const CLIENT_INTERNAL_NOTE_ENTRY_SEPARATOR = '\n\n---\n\n';

export type ClientClassificationCode =
  | 'CLIENTE_CRITICO'
  | 'CLIENTE_SENSIVEL'
  | 'CLIENTE_REGULAR'
  | 'CLIENTE_VIP_ESTRATEGICO';

export interface ClientClassificationMeta {
  code: ClientClassificationCode;
  label: string;
  color: 'red' | 'yellow' | 'blue' | 'green';
}

const CLASSIFICATION_META_BY_CODE: Record<ClientClassificationCode, ClientClassificationMeta> = {
  CLIENTE_CRITICO: {
    code: 'CLIENTE_CRITICO',
    label: 'Cliente Crítico',
    color: 'red',
  },
  CLIENTE_SENSIVEL: {
    code: 'CLIENTE_SENSIVEL',
    label: 'Cliente Sensível',
    color: 'yellow',
  },
  CLIENTE_REGULAR: {
    code: 'CLIENTE_REGULAR',
    label: 'Cliente Regular',
    color: 'blue',
  },
  CLIENTE_VIP_ESTRATEGICO: {
    code: 'CLIENTE_VIP_ESTRATEGICO',
    label: 'Cliente VIP/Estratégico',
    color: 'green',
  },
};

const CLASSIFICATION_ALIASES: Record<string, ClientClassificationCode> = {
  CLIENTE_CRITICO: 'CLIENTE_CRITICO',
  CLIENTE_SENSIVEL: 'CLIENTE_SENSIVEL',
  CLIENTE_REGULAR: 'CLIENTE_REGULAR',
  CLIENTE_VIP_ESTRATEGICO: 'CLIENTE_VIP_ESTRATEGICO',
  CLIENTE_CRITICO_: 'CLIENTE_CRITICO',
  CRITICO: 'CLIENTE_CRITICO',
  SENSIVEL: 'CLIENTE_SENSIVEL',
  REGULAR: 'CLIENTE_REGULAR',
  VIP_ESTRATEGICO: 'CLIENTE_VIP_ESTRATEGICO',
};

export const CLIENT_CLASSIFICATION_OPTIONS: ClientClassificationMeta[] = [
  CLASSIFICATION_META_BY_CODE.CLIENTE_CRITICO,
  CLASSIFICATION_META_BY_CODE.CLIENTE_SENSIVEL,
  CLASSIFICATION_META_BY_CODE.CLIENTE_REGULAR,
  CLASSIFICATION_META_BY_CODE.CLIENTE_VIP_ESTRATEGICO,
];

const sanitizeClassificationKey = (raw: string): string =>
  raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[/-]/g, '_')
    .toUpperCase();

export const normalizeClientClassificationCode = (
  value?: string | null
): ClientClassificationCode => {
  if (!value || value.trim() === '') return 'CLIENTE_REGULAR';
  const normalized = sanitizeClassificationKey(value.trim());
  return CLASSIFICATION_ALIASES[normalized] ?? 'CLIENTE_REGULAR';
};

export const getClientClassificationMeta = (
  value?: string | null
): ClientClassificationMeta => {
  const code = normalizeClientClassificationCode(value);
  return CLASSIFICATION_META_BY_CODE[code];
};

export const readClientClassification = (target: unknown): string | null => {
  if (!target || typeof target !== 'object') return null;
  const source = target as Record<string, unknown>;
  const raw =
    source.clientClassification ??
    source.classificacaoCliente ??
    source.classification ??
    source.classificacao ??
    source.clienteClassificacao;

  return typeof raw === 'string' ? raw : null;
};

export const readClientInternalNotes = (target: unknown): string => {
  if (!target || typeof target !== 'object') return '';
  const source = target as Record<string, unknown>;
  const raw =
    source.internalNotes ??
    source.clientInternalNotes ??
    source.observacoesInternas ??
    source.observacaoInterna;

  return typeof raw === 'string' ? raw : '';
};

export const readContractInternalNotes = (target: unknown): string => {
  if (!target || typeof target !== 'object') return '';
  const source = target as Record<string, unknown>;
  const raw =
    source.internalNotes ??
    source.contractInternalNotes ??
    source.observacoesInternasContrato ??
    source.observacaoInternaContrato;
  return typeof raw === 'string' ? raw : '';
};

export const parseClientInternalNoteEntries = (notes: string): string[] => {
  const trimmed = notes.trim();
  if (!trimmed) return [];
  if (trimmed.includes(CLIENT_INTERNAL_NOTE_ENTRY_SEPARATOR)) {
    return trimmed
      .split(CLIENT_INTERNAL_NOTE_ENTRY_SEPARATOR)
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  return [trimmed];
};

export const joinClientInternalNoteEntries = (entries: string[]): string => {
  const cleaned = entries.map((entry) => entry.trim()).filter(Boolean);
  if (!cleaned.length) return '';
  if (cleaned.length === 1) return cleaned[0];
  return cleaned.join(CLIENT_INTERNAL_NOTE_ENTRY_SEPARATOR);
};

export const countClientInternalNoteEntries = (notes: string): number =>
  parseClientInternalNoteEntries(notes).length;

export const appendClientInternalNoteEntry = (existing: string, newEntry: string): string => {
  const entry = newEntry.trim();
  if (!entry) return existing.trim();
  return joinClientInternalNoteEntries([...parseClientInternalNoteEntries(existing), entry]);
};

export const buildClientUpdatePayloadFromResponse = (
  client: ClientResponse
): UpdateClientRequest => ({
  tipoCliente: client.tipoCliente,
  segmento: client.segmento,
  cnpj: client.cnpj,
  razaoSocial: client.razaoSocial,
  nomeFantasia: client.nomeFantasia,
  clientClassification: normalizeClientClassificationCode(readClientClassification(client)),
  internalNotes: readClientInternalNotes(client) || undefined,
  tipo: client.tipo,
  porte: client.porte,
  dataAberturaEmpresa: client.dataAberturaEmpresa,
  naturezaJuridica: client.naturezaJuridica,
  ramoAtividade: client.ramoAtividade,
  ramoAtividadeSecundaria: client.ramoAtividadeSecundaria,
  situacaoCadastral: client.situacaoCadastral,
  motivoSituacaoCadastral: client.motivoSituacaoCadastral,
  dataSituacaoCadastral: client.dataSituacaoCadastral,
  situacaoEspecial: client.situacaoEspecial,
  dataSituacaoEspecial: client.dataSituacaoEspecial,
  capitalSocial: client.capitalSocial,
  telefone1: client.telefone1,
  telefone2: client.telefone2,
  email: client.email,
  cep: client.cep,
  estado: client.estado,
  cidade: client.cidade,
  logradouro: client.logradouro,
  numero: client.numero,
  bairro: client.bairro,
  complemento: client.complemento,
  pontoReferencia: client.pontoReferencia,
});
